
import React, { useEffect, useState } from 'react';
import styled, { keyframes, createGlobalStyle, css } from 'styled-components';
import { 
  C, 
  TITLES, 
  RedesignFonts,
  ContractAddress, 
  Buy, 
  Socials, 
  UTILITIES, 
  ROADMAP,
  MEMES,
  COMIC_BOOK,
  VIDEOS,
  NDHAUS_PHOTO
} from '../kit';
import { CONTENT } from '../content';
import { useTokenomics } from '../useTokenomics';
import { ComicReader } from '../comic/ComicReader';

const MobileGlobal = createGlobalStyle`
  :root {
    --safe-bottom: env(safe-area-inset-bottom, 20px);
    --safe-top: env(safe-area-inset-top, 20px);
  }
  html, body {
    margin: 0;
    padding: 0;
    background-color: ${C.ink};
    color: ${C.white};
    font-family: 'Ubuntu', sans-serif;
    -webkit-tap-highlight-color: transparent;
    overscroll-behavior-y: none;
    scroll-behavior: smooth;
  }
  * { box-sizing: border-box; }
  a { text-decoration: none; color: inherit; }
  input, button, textarea, select { font-size: 16px; }
  
  ::-webkit-scrollbar { width: 0px; background: transparent; }
  
  .reveal {
    opacity: 0;
    transform: translateY(40px);
    transition: opacity 0.8s cubic-bezier(0.22, 1, 0.36, 1),
                transform 0.8s cubic-bezier(0.22, 1, 0.36, 1);
  }
  .reveal.visible {
    opacity: 1;
    transform: translateY(0);
  }
  @media (prefers-reduced-motion: reduce) {
    .reveal { transition: none; transform: none; opacity: 1; }
    .title-art { animation: none !important; }
  }
`;

const Layout = styled.div`
  width: 100vw;
  overflow-x: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
  
  /* The Descent Gradient */
  background: linear-gradient(
    180deg,
    #0d1726 0%,      /* Night */
    #142b4a 15%,     /* Pond surface */
    #0e1e35 40%,     /* Deep water */
    #0a1424 70%,     /* Murky depth */
    #050a11 100%     /* Swamp floor */
  );
`;

const float = keyframes`
  0% { transform: translateY(0); }
  50% { transform: translateY(-8px); }
  100% { transform: translateY(0); }
`;

const sway = keyframes`
  0% { transform: rotate(-2deg); }
  50% { transform: rotate(2deg); }
  100% { transform: rotate(-2deg); }
`;

/* ----------------------------------------------------------------- Dock */
const Dock = styled.nav`
  position: fixed;
  bottom: calc(var(--safe-bottom) + 12px);
  left: 12px;
  right: 12px;
  z-index: 100;
  display: flex;
  justify-content: space-between;
  gap: 0;
  background: rgba(14, 27, 51, 0.85);
  backdrop-filter: blur(12px);
  padding: 6px 8px;
  border-radius: 40px;
  border: 2px solid ${C.blue};
  box-shadow: 0 8px 32px rgba(0,0,0,0.6);
  
  a, button {
    background: none;
    border: none;
    color: ${C.white};
    font-family: 'GROBOLD', sans-serif;
    font-size: 11px;
    letter-spacing: 0;
    padding: 12px 6px;
    min-height: 44px;
    justify-content: center;
    cursor: pointer;
    text-transform: uppercase;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    
    &:active {
      transform: scale(0.95);
      color: ${C.yellow};
    }
  }
`;

const SubMenu = styled.div<{ $open: boolean }>`
  position: absolute;
  bottom: 120%;
  right: 0;
  background: ${C.ink};
  border: 2px solid ${C.pink};
  border-radius: 20px;
  padding: 16px;
  display: ${p => p.$open ? 'flex' : 'none'};
  flex-direction: column;
  gap: 16px;
  box-shadow: 0 8px 32px rgba(0,0,0,0.5);
  
  a {
    font-family: 'Ubuntu', sans-serif;
    font-weight: 700;
    font-size: 16px;
    padding: 0;
    align-items: flex-start;
  }
`;

const DockMenu = () => {
  const [open, setOpen] = useState(false);
  return (
    <Dock>
      <a href="#hero">HOME</a>
      <a href="#dollarize">DOLLARIZE</a>
      <a href="#tokenomics">TOKENOMICS</a>
      <a href="#roadmap">ROADMAP</a>
      <div style={{position: 'relative'}}>
        <button onClick={() => setOpen(!open)}>MORE</button>
        <SubMenu $open={open} onClick={() => setOpen(false)}>
          <a href="/ens">ENS</a>
          <a href="/governance">Governance</a>
          <a href={CONTENT.menu.earn}>Earn</a>
          <a href={CONTENT.menu.bridge}>Bridge</a>
        </SubMenu>
      </div>
    </Dock>
  );
}

/* ----------------------------------------------------------------- Scroll Progress */
const ZeusScroller = styled.div<{ $progress: number }>`
  position: fixed;
  top: ${p => Math.max(10, Math.min(90, p.$progress * 100))}%;
  right: 12px;
  width: 48px;
  height: 48px;
  background: url('/redesign2/tokenomics/supply.webp') center/contain no-repeat;
  z-index: 99;
  transition: top 0.1s linear;
  pointer-events: none;
  filter: drop-shadow(0 4px 10px rgba(0,0,0,0.5));
  animation: ${sway} 3s ease-in-out infinite;
`;

const useScrollProgress = () => {
  const [p, setP] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const total = document.documentElement.scrollHeight - window.innerHeight;
      setP(total > 0 ? window.scrollY / total : 0);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  return p;
};

const useReveals = () => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, { threshold: 0.1 });
    document.querySelectorAll('.reveal').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);
};

/* ----------------------------------------------------------------- Sections */
const Section = styled.section`
  width: 100vw;
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 60px 20px;
  z-index: 2;
`;

const HeroSection = styled(Section)`
  padding-top: max(var(--safe-top), 80px);
  min-height: 110dvh;
  justify-content: flex-start;
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: url('/redesign2/scenes/camp.webp') center bottom / cover no-repeat;
    z-index: -1;
    mask-image: linear-gradient(to bottom, black 80%, transparent 100%);
    -webkit-mask-image: linear-gradient(to bottom, black 80%, transparent 100%);
  }

  .title-art {
    width: 90%;
    max-width: 340px;
    margin-bottom: 40px;
    animation: ${float} 4s ease-in-out infinite;
    filter: drop-shadow(0 8px 16px rgba(0,0,0,0.6));
  }
  
  .hero-card {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 20px;
    width: 100%;
    max-width: 400px;
    background: rgba(0,0,0,0.4);
    padding: 24px;
    border-radius: 20px;
    backdrop-filter: blur(4px);
    border: 2px dashed rgba(255,255,255,0.2);
  }
`;

const DescSection = styled(Section)`
  h2 {
    font-family: 'GROBOLD', sans-serif;
    color: ${C.yellow};
    font-size: 32px;
    margin: 0 0 24px 0;
    text-align: center;
    text-shadow: 2px 2px 0 ${C.ink};
  }
  p {
    font-size: 18px;
    line-height: 1.6;
    margin: 0 0 16px 0;
    text-align: center;
    max-width: 500px;
    text-shadow: 1px 1px 4px rgba(0,0,0,0.8);
  }
  .video-container {
    width: 100%;
    max-width: 500px;
    border-radius: 16px;
    overflow: hidden;
    border: 4px solid ${C.ink};
    margin: 32px 0;
    background: #000;
    box-shadow: 0 10px 20px rgba(0,0,0,0.5);
  }
  video {
    width: 100%;
    display: block;
  }
`;

/* ----------------------------------------------------------------- Tokenomics */
const FACTS = [
  { key: 'supply', big: '420.69T', label: 'FIXED SUPPLY', note: '420 690 000 000 000 $ZEUS', link: CONTENT.tokenomics.cards[3].link, art: '/redesign2/tokenomics/supply.webp' },
  { key: 'tax', big: '0%', label: 'TAX', note: 'No tax on buys or sells', link: CONTENT.tokenomics.cards[2].link, art: '/redesign2/tokenomics/tax.webp' },
  { key: 'locked', big: 'LOCKED', label: 'CONTRACT', note: CONTENT.tokenomics.cards[1].text, link: CONTENT.tokenomics.cards[1].link, art: '/redesign2/tokenomics/locked.webp' },
  { key: 'burned', big: 'BURNED', label: 'LP RENOUNCED', note: CONTENT.tokenomics.cards[0].text, link: CONTENT.tokenomics.cards[0].link, art: '/redesign2/tokenomics/burned.webp' },
];

const PieSvg = styled.svg`
  display: block;
  margin: 0 auto;
  overflow: visible;
  filter: drop-shadow(0 10px 20px rgba(0,0,0,0.5));
`;
const slice = (cx: number, cy: number, r: number, from: number, to: number) => {
  const a0 = ((from - 90) * Math.PI) / 180;
  const a1 = ((to - 90) * Math.PI) / 180;
  const large = to - from > 180 ? 1 : 0;
  return `M${cx} ${cy} L${cx + r * Math.cos(a0)} ${cy + r * Math.sin(a0)} A${r} ${r} 0 ${large} 1 ${cx + r * Math.cos(a1)} ${cy + r * Math.sin(a1)} Z`;
};
const Pie = ({ data, size = 260 }: { data: any, size?: number }) => {
  const lpDeg = data ? (data.lpZeusPct / 100) * 360 : 0;
  const span = Math.max(lpDeg, 4);
  const from = 0;
  return (
    <PieSvg viewBox="0 0 220 220" width={size} height={size}>
      <circle cx="110" cy="110" r="100" fill={data ? C.sky : '#dde6f2'} stroke={C.ink} strokeWidth="5" />
      {data && (
        <path d={slice(110, 110, 100, from, from + span)} fill={C.yellow} stroke={C.ink} strokeWidth="5" strokeLinejoin="round" />
      )}
      <circle cx="110" cy="110" r="5" fill={C.ink} />
    </PieSvg>
  );
};

const FloatingFactGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
  width: 100%;
  margin-top: 60px;
  position: relative;
`;
const FloatingFact = styled.a<{ $offset: number }>`
  display: flex;
  align-items: center;
  gap: 16px;
  color: ${C.white};
  transform: translateX(${p => p.$offset}px);
  position: relative;
  
  .art {
    width: 100px;
    height: 100px;
    object-fit: contain;
    filter: drop-shadow(0 10px 15px rgba(0,0,0,0.6));
    animation: ${float} ${p => 3 + Math.abs(p.$offset)/10}s ease-in-out infinite;
  }
  .info {
    display: flex;
    flex-direction: column;
    padding: 8px;
    text-shadow: 2px 2px 4px rgba(0,0,0,0.8);
  }
  .big {
    font-family: 'Titan One', sans-serif;
    font-size: 26px;
    color: ${C.pink};
    -webkit-text-stroke: 1px ${C.ink};
  }
  .label {
    font-family: 'GROBOLD', sans-serif;
    font-size: 14px;
    color: ${C.yellow};
  }
`;

const TokenomicsSection = () => {
  const { data } = useTokenomics();
  return (
    <DescSection id="tokenomics">
      <h2 className="reveal">WHERE'S THE SUPPLY?</h2>
      <div className="reveal" style={{ position: 'relative', margin: '20px 0' }}>
        <Pie data={data} />
        {data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', marginTop: 20 }}>
            <div style={{ background: C.ink, padding: '8px 16px', borderRadius: 20, border: `2px solid ${C.yellow}`, fontFamily: 'GROBOLD', fontSize: 14 }}>
              <span style={{ color: C.yellow, marginRight: 8 }}>●</span> LP LOCKED: {data.lpZeusPct.toFixed(2)}%
            </div>
            <div style={{ background: C.ink, padding: '8px 16px', borderRadius: 20, border: `2px solid ${C.sky}`, fontFamily: 'GROBOLD', fontSize: 14 }}>
              <span style={{ color: C.sky, marginRight: 8 }}>●</span> CIRCULATING: {data.circulatingPct.toFixed(2)}%
            </div>
          </div>
        )}
      </div>
      <FloatingFactGrid>
        {FACTS.map((f, i) => (
          <FloatingFact key={f.key} href={f.link} target="_blank" $offset={i % 2 === 0 ? -10 : 10} className="reveal">
            {i % 2 === 0 && <img src={f.art} className="art" alt="" />}
            <div className="info">
              <span className="big">{f.big}</span>
              <span className="label">{f.label}</span>
            </div>
            {i % 2 !== 0 && <img src={f.art} className="art" alt="" />}
          </FloatingFact>
        ))}
      </FloatingFactGrid>
    </DescSection>
  );
};

/* ----------------------------------------------------------------- Dollarize */
const Dollarize = styled(Section)`
  position: relative;
  padding: 100px 20px;
  
  &::before {
    content: '';
    position: absolute;
    top: 50%; left: 50%;
    transform: translate(-50%, -50%);
    width: 150vw; height: 150vw;
    background: url('/redesign2/scenes/blackhole.webp') center/contain no-repeat;
    opacity: 0.6;
    z-index: -1;
    pointer-events: none;
    animation: ${sway} 10s ease-in-out infinite;
  }
  
  .content {
    background: rgba(14,27,51,0.7);
    padding: 40px 24px;
    border-radius: 255px 15px 225px 15px / 15px 225px 15px 255px;
    border: 3px solid ${C.sky};
    text-align: center;
    backdrop-filter: blur(8px);
    max-width: 500px;
  }
  h2 { font-family: 'GROBOLD', sans-serif; color: ${C.yellow}; font-size: 28px; margin: 0 0 24px 0; text-shadow: 2px 2px 0 ${C.ink}; }
  p { font-size: 16px; line-height: 1.6; margin: 0 0 16px 0; }
  .btn {
    display: inline-block;
    background: ${C.pink};
    color: white;
    font-family: 'GROBOLD', sans-serif;
    padding: 16px 32px;
    border-radius: 40px;
    border: 3px solid ${C.ink};
    font-size: 20px;
    margin-top: 24px;
    box-shadow: 4px 4px 0 ${C.ink};
    &:active { transform: translate(2px, 2px); box-shadow: 2px 2px 0 ${C.ink}; }
  }
`;

/* ----------------------------------------------------------------- Roadmap */
const RoadmapWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-top: 40px;
`;
const MilestoneObj = styled.div<{ $done: boolean }>`
  background: ${p => p.$done ? 'rgba(14, 27, 51, 0.5)' : 'rgba(14, 27, 51, 0.9)'};
  border: 3px solid ${p => p.$done ? C.sky : C.yellow};
  border-radius: 15px 255px 15px 225px / 255px 15px 225px 15px;
  padding: 20px;
  color: ${C.white};
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    left: -10px; top: 20px;
    width: 20px; height: 20px;
    background: ${p => p.$done ? C.sky : C.yellow};
    border: 3px solid ${C.ink};
    border-radius: 50%;
  }
  
  .title {
    font-family: 'GROBOLD', sans-serif;
    font-size: 18px;
    margin-bottom: 12px;
    color: ${p => p.$done ? C.sky : C.yellow};
  }
  .desc {
    font-family: 'Ubuntu', sans-serif;
    font-size: 15px;
    line-height: 1.5;
  }
`;
const SwipeableMiddle = styled.div`
  display: flex;
  overflow-x: auto;
  gap: 16px;
  padding: 16px 20px;
  margin: 0 -20px;
  snap-type: x mandatory;
  &::-webkit-scrollbar { display: none; }
  > * {
    scroll-snap-align: center;
    min-width: 85vw;
  }
`;

const RoadmapSection = () => {
  const first = ROADMAP[0];
  const last = ROADMAP[ROADMAP.length - 1];
  const middle = ROADMAP.slice(1, -1);
  return (
    <DescSection id="roadmap">
      <h2 className="reveal">ROADMAP</h2>
      <RoadmapWrapper>
        <MilestoneObj className="reveal" $done={first.milestone.status === 'shipped'}>
          <div className="title">{first.lines.join(' ')}</div>
          <div className="desc">{first.milestone.description}</div>
        </MilestoneObj>
        <SwipeableMiddle className="reveal">
          {middle.map((m, i) => (
            <MilestoneObj key={i} $done={m.milestone.status === 'shipped'}>
              <div className="title">{m.lines.join(' ')}</div>
              <div className="desc">{m.milestone.description}</div>
            </MilestoneObj>
          ))}
        </SwipeableMiddle>
        <MilestoneObj className="reveal" $done={last.milestone.status === 'shipped'}>
          <div className="title">{last.lines.join(' ')}</div>
          <div className="desc">{last.milestone.description}</div>
        </MilestoneObj>
      </RoadmapWrapper>
    </DescSection>
  );
};

/* ----------------------------------------------------------------- Memes */
const MemesSection = styled(Section)`
  position: relative;
  padding-top: 100px;
  padding-bottom: 180px; /* Space for the dock */
  
  &::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0; bottom: 0;
    background: url('/redesign2/scenes/swamp-empty.webp') center top / cover no-repeat;
    z-index: -1;
    mask-image: linear-gradient(to top, black 80%, transparent 100%);
    -webkit-mask-image: linear-gradient(to top, black 80%, transparent 100%);
    opacity: 0.8;
  }
  
  .grid {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 16px;
    width: 100%;
    margin-top: 40px;
  }
  .meme {
    width: 100%;
    border-radius: 12px;
    border: 3px solid ${C.ink};
    background: ${C.ink};
    box-shadow: 4px 4px 0 ${C.ink};
  }
`;

/* ----------------------------------------------------------------- Comic & NFT */
const ArtSection = styled(Section)`
  .photo {
    width: 140px;
    border-radius: 50%;
    border: 4px dashed ${C.yellow};
    margin: 20px 0 30px;
    animation: ${float} 5s ease-in-out infinite;
  }
  .nft-hero {
    width: 100%;
    border-radius: 20px;
    border: 4px solid ${C.sky};
    margin: 32px 0;
    box-shadow: 0 10px 20px rgba(0,0,0,0.5);
  }
  .read-btn {
    background: ${C.yellow};
    color: ${C.ink};
    font-family: 'Titan One', sans-serif;
    font-size: 24px;
    padding: 20px;
    border-radius: 20px;
    border: 4px solid ${C.ink};
    width: 100%;
    text-align: center;
    margin: 32px 0;
    box-shadow: 4px 4px 0 ${C.ink};
    &:active { transform: translate(2px, 2px); box-shadow: 2px 2px 0 ${C.ink}; }
  }
`;

/* ----------------------------------------------------------------- Join */
const JoinSection = styled(Section)`
  min-height: auto;
  padding-bottom: 200px; /* Dock clearance */
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -50px; left: 0; right: 0; bottom: 0;
    background: url('/redesign2/scenes/gang.webp') center bottom / cover no-repeat;
    z-index: -1;
    mask-image: linear-gradient(to top, black 80%, transparent 100%);
    -webkit-mask-image: linear-gradient(to top, black 80%, transparent 100%);
  }
  
  .box {
    background: rgba(14,27,51,0.85);
    padding: 40px 20px;
    border-radius: 255px 15px 225px 15px / 15px 225px 15px 255px;
    border: 3px dashed ${C.pink};
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    gap: 30px;
    backdrop-filter: blur(8px);
    margin-top: 40px;
  }
`;

export default function RedesignM1() {
  const p = useScrollProgress();
  useReveals();
  const [comicOpen, setComicOpen] = useState(false);
  
  return (
    <Layout>
      <MobileGlobal />
      <RedesignFonts />
      <ZeusScroller $progress={p} />
      <DockMenu />
      
      {/* Hero */}
      <HeroSection id="hero">
        <img src={TITLES.zeus.src} alt="ZEUS" className="title-art reveal" />
        
        <div className="hero-card reveal">
          <ContractAddress tone="light" />
          <Buy big />
          <div style={{ display: 'flex', gap: 16, marginTop: 16 }}>
            <Socials color={C.white} size={36} />
          </div>
        </div>
      </HeroSection>
      
      {/* About */}
      <DescSection>
        <h2 className="reveal">THE DOG</h2>
        {CONTENT.about.paragraphs.map((p, i) => <p key={i} className="reveal">{p}</p>)}
        <div className="video-container reveal">
          <video src={VIDEOS.trailer.src} controls playsInline poster={VIDEOS.trailer.poster} />
        </div>
        <p className="reveal"><a href={CONTENT.about.trademarkLink} target="_blank" style={{color: C.sky, textDecoration: 'underline'}}>Trademark Info</a></p>
      </DescSection>
      
      {/* CTO */}
      <DescSection>
        <img src={TITLES.takeOver.src} alt="Community Take Over" className="reveal" style={{ width: '80%', marginBottom: 32, filter: 'drop-shadow(0 4px 10px rgba(0,0,0,0.5))' }} />
        {CONTENT.aboutCto.paragraphs.map((p, i) => <p key={i} className="reveal">{p}</p>)}
        <div className="video-container reveal">
          <video src={VIDEOS.buy.src} controls playsInline poster={VIDEOS.buy.poster} />
        </div>
      </DescSection>
      
      {/* Tokenomics */}
      <TokenomicsSection />
      
      {/* Dollarize */}
      <Dollarize id="dollarize">
        <div className="content reveal">
          <h2>{CONTENT.dollarize.title}</h2>
          {CONTENT.dollarize.paragraphs.map((p, i) => <p key={i}>{p}</p>)}
          <a className="btn" href={CONTENT.dollarize.mintLink} target="_blank">MINT USDZEUS</a>
          <br/>
          <a href={CONTENT.dollarize.howLink} target="_blank" style={{ color: C.sky, textDecoration: 'underline', marginTop: 24, display: 'inline-block' }}>How it works</a>
        </div>
      </Dollarize>
      
      {/* Roadmap */}
      <RoadmapSection />
      
      {/* Artist & NFTs */}
      <ArtSection>
        <h2 className="reveal">THE ARTIST</h2>
        <img src={NDHAUS_PHOTO} className="photo reveal" alt="ndhaus" />
        {CONTENT.artist.paragraphs.map((p, i) => <p key={i} className="reveal">{p}</p>)}
        
        <h2 className="reveal" style={{ marginTop: 80 }}>{CONTENT.nfts.title}</h2>
        {CONTENT.nfts.paragraphs.map((p, i) => <p key={i} className="reveal">{p}</p>)}
        <img src="/redesign2/nfts/hero.webp" className="nft-hero reveal" alt="NFTs" />
        <a href={CONTENT.nfts.link} target="_blank" className="reveal" style={{ color: C.sky, textDecoration: 'underline' }}>View on OpenSea</a>
        
        <button className="read-btn reveal" onClick={() => setComicOpen(true)}>READ THE COMIC</button>
        <a href="/zeus.pdf" className="reveal" download style={{ color: C.mist, textDecoration: 'underline', margin: '-16px 0 40px', display: 'block', textAlign: 'center' }}>Download PDF</a>
      </ArtSection>
      
      {/* Memes */}
      <MemesSection>
        <h2 className="reveal" style={{ fontFamily: 'GROBOLD', color: C.yellow, fontSize: 36, textAlign: 'center', margin: 0, textShadow: `3px 3px 0 ${C.ink}` }}>THE SWAMP</h2>
        <div className="grid">
          {MEMES.map((m, i) => (
            <img key={i} src={m.src} className="meme reveal" alt="Meme" loading="lazy" />
          ))}
        </div>
      </MemesSection>
      
      {/* Join */}
      <JoinSection>
        <div className="box reveal">
          <h2 style={{ fontFamily: 'GROBOLD', color: C.yellow, fontSize: 32, margin: 0, textShadow: `2px 2px 0 ${C.ink}` }}>JOIN THE GANG</h2>
          <Buy big />
          <div style={{ display: 'flex', gap: 16 }}>
            <Socials color={C.white} size={40} />
          </div>
        </div>
      </JoinSection>
      
      {comicOpen && <ComicReader pages={COMIC_BOOK} startPage={0} pdf="/zeus.pdf" onClose={() => setComicOpen(false)} />}
    </Layout>
  );
}
